export const enum FINANCE_FAMILY_STATUS {
  SINGLE = 'single',
  MARRIED = 'married',
  DE_FACTO = 'de_facto',
  WIDOWED = 'widowed',
  DIVORCED = 'divorced',
  SEPARATED = 'separated',
  OTHER = 'other',
}

export const RESOLUTION_OF_STATUS_VISA_SUBCLASS = '851'; // This is a permanent visa, as it will have no expiry date

export const enum FINANCE_RESIDENTIAL_STATUS {
  HOMEOWNER = 'home_owner',
  RENTING = 'renting',
  BOARDER = 'boarding',
  LIVING_WITH_PARENTS = 'living_with_parents',
  EMPLOYER_SUPPLIED_ACCOMMODATION = 'supplied_by_employer',
}

export const enum FINANCE_EMPLOYMENT_STATUS {
  FULL_TIME = 'full_time',
  PART_TIME = 'part_time',
  CASUAL = 'casual',
  CONTRACT = 'contract',
  SELF_EMPLOYED = 'self_employed',
  RETIRED = 'retired',
  UNEMPLOYED = 'unemployed',
}

export const enum FINANCE_RESIDENCY_STATUS {
  AUSTRALIAN_CITIZEN = 'australian_citizen',
  PERMANENT_RESIDENT = 'permanent_resident',
  TEMPORARY_RESIDENT = 'temporary_resident',
}

export interface FinanceQuoteRequestPayload {
  is_personalised_quote?: boolean; // flag used to send pubsub message to hubspot-sync from finance-api for personalised quote form
  loan_amount_cents: number;
  vehicle: {
    year: string;
    make?: string | null;
    model?: string | null;
    price_cents: number;
    sku?: string;
  };
  applicant: {
    first_name: string;
    middle_name?: string;
    last_name: string;
    date_of_birth: string;
    driver_licence?: string;
    email: string;
    phone: string;
    current_address: {
      address: {
        unit: string;
        street_number: string;
        street_name: string;
        suburb: string;
        postcode: string;
        state: string;
      };
      start_date: string;
    };
    previous_address?: {
      address: {
        unit: string;
        street_number: string;
        street_name: string;
        suburb: string;
        postcode: string;
        state: string;
      };
      start_date: string;
    };
    current_address_status: FINANCE_RESIDENTIAL_STATUS;
    has_mortgage_on_current_address?: boolean;
    has_investment_property?: boolean;
    has_mortgage_on_investment_property?: boolean;
    personal?: {
      marital_status: FINANCE_FAMILY_STATUS;
      income_cents: number;
      has_tenancy_agreement?: boolean;
      investment_property?: {
        address: {
          unit: string;
          street_number: string;
          street_name: string;
          suburb: string;
          postcode: string;
          state: string;
        };
      };
      current_employment: {
        status: FINANCE_EMPLOYMENT_STATUS;
        job_title: string;
        start_date: string;
      };
    };
    business?: {
      abn: string;
    };
    residency_status?: FINANCE_RESIDENCY_STATUS;
    visa?: {
      code: string;
      end_date?: string;
    };
  };
}

export interface FinanceQuoteRequestResponse {
  id: string;
  key: string;
}

export const enum FINANCE_QUOTE_PROVIDER {
  ALLIED = 'allied',
  PLENTI = 'plenti',
  WISR = 'wisr',
  CARMA = 'carma',
  VWFS = 'vwfs',
}

export interface FinanceQuote {
  quote_id: string;
  financier?: FINANCE_QUOTE_PROVIDER | 'youx';
  offer: {
    rate: number;
    repayment_amount_cents: number;
    adjusted_repayment_amount_cents?: number;
    term_months: number;
    fees?: {
      broker_fee_cents: number;
      establishment_fee_cents: number;
      ppsr_fee_cents: number;
      repayment_fee_cents: number;
    };
  };
  refusal?: RefusalError[] | null;
  state?: string;
}

export interface RefusalError {
  key: string;
  message: string;
}

interface ProviderFinanceQuoteRequestStatus {
  provider: string;
  total_quotes: number;
  submitted: number;
  pending: number;
}

interface FinanceQuoteRequestStatus {
  lenders: number;
  completed_lenders: number;
  has_completed: boolean;
  total_requests_submitted: number;
  total_requests_pending: number;
  data: ProviderFinanceQuoteRequestStatus[];
}

export interface FinanceQuoteResponse {
  quotes: FinanceQuote[];
  status?: FinanceQuoteRequestStatus;
}

export type ExtendedCoverageFinancePayload = {
  amendments: Amendment[];
  interest_rate: number;
  term: number;
  current_monthly_repayment_amount_cents: number;
};

type Amendment = {
  external_id: string;
  type: 'product' | 'discount' | 'custom';
};

export type ExtendedCoverageFinanceResponse = {
  original_monthly_repayment_amount_cents: number;
  original_weekly_repayment_amount_cents: number;
  adjusted_monthly_repayment_amount_cents: number;
  adjusted_weekly_repayment_amount_cents: number;
  amendments: Amendment[];
};
